import { FC, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';

interface DashboardRouteProps {
  children: React.ReactNode;
}

/**
 * Guard das rotas dos painéis executivos (`/paineis/...`).
 *
 * Os painéis não entram na Sidebar — chega-se neles por link direto —, então este
 * guard é a primeira coisa que um usuário sem liberação encontra. A checagem é
 * redundante com o backend (403 estruturado `dashboard_feature_not_enabled`); aqui
 * é UX, para não deixar o usuário bater na 403 sem entender por quê.
 *
 * Diferente do Balanceamento de Linha, só ADMIN passa sem flag: CURATOR precisa
 * ser liberado como qualquer outro. A regra mora no backend
 * (`dashboard_feature_enabled` do `/auth/me/`); `undefined` conta como bloqueado.
 */
const DashboardRoute: FC<DashboardRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const { showToast } = useToast();

  const blocked = !isLoading && isAuthenticated && user && !user.dashboard_feature_enabled;

  useEffect(() => {
    if (blocked) {
      showToast('Os painéis ainda não foram liberados pra você. Fale com seu admin.', 'warning');
    }
  }, [blocked, showToast]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (blocked) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default DashboardRoute;
